import { motion, useTransform } from 'framer-motion';
import { Scene, useSpan } from '../motion';
import PlayingCard from '../components/PlayingCard';
import { NET_SERIES, TOP_HANDS, formatMoney } from '../demoData';

const W = 1000;
const H = 320;
const X_MAX = NET_SERIES[NET_SERIES.length - 1][0];
const Y_MIN = -10;
const Y_MAX = 40;
const sx = hand => ((hand - 1) / (X_MAX - 1)) * W;
const sy = net => H - ((net - Y_MIN) / (Y_MAX - Y_MIN)) * H;
const PATH = NET_SERIES.map(([h, n], i) => `${i ? 'L' : 'M'}${sx(h).toFixed(1)} ${sy(n).toFixed(1)}`).join(' ');

const DRAW = [0.05, 0.62];
const reachedAt = hand => DRAW[0] + ((hand - 1) / (X_MAX - 1)) * (DRAW[1] - DRAW[0]);

function Marker({ p, hand, positive }) {
  const net = NET_SERIES.find(([h]) => h === hand)[1];
  const t = useSpan(p, reachedAt(hand), reachedAt(hand) + 0.04);
  return (
    <motion.span
      aria-hidden="true"
      className={`absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-4 ring-pn-raised ${positive ? 'bg-pn-signal' : 'bg-pn-loss'}`}
      style={{ left: `${(sx(hand) / W) * 100}%`, top: `${(sy(net) / H) * 100}%`, scale: t }}
    />
  );
}

function HandSummary({ p, data, at }) {
  const t = useSpan(p, at, at + 0.08);
  const y = useTransform(t, [0, 1], [24, 0]);
  const win = data.net > 0;
  return (
    <motion.div style={{ opacity: t, y }} className="pn-bezel">
      <div className="pn-plate p-[clamp(0.9rem,0.5rem+1.2*var(--u),1.5rem)]">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-bold">Hand #{data.hand}</p>
          <p className={`pn-num text-[clamp(1.1rem,0.8rem+1.2*var(--u),1.9rem)] font-bold ${win ? 'text-pn-signal' : 'text-pn-loss'}`}>
            {formatMoney(data.net, { sign: true })}
          </p>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="flex gap-1">{data.cards.map(c => <PlayingCard key={c} card={c} size="sm" />)}</span>
          <span className="flex gap-1 opacity-90">{data.board.map(c => <PlayingCard key={c} card={c} size="sm" />)}</span>
        </div>
        <p className="pn-caption mt-3">
          vs {data.opponent} · in {formatMoney(data.invested)} · out {formatMoney(data.collected)}
        </p>
      </div>
    </motion.div>
  );
}

export default function HandReplay() {
  return (
    <Scene id="hands" length={2.4} label="Hand results">
      {p => <Frame p={p} />}
    </Scene>
  );
}

function Frame({ p }) {
  const pathLength = useSpan(p, DRAW[0], DRAW[1]);
  const total = useTransform(pathLength, v => {
    // Running total at the tip of the line.
    const hand = 1 + v * (X_MAX - 1);
    let last = NET_SERIES[0][1];
    for (const [h, n] of NET_SERIES) if (h <= hand) last = n;
    return formatMoney(last, { sign: true });
  });

  return (
    <div className="mx-auto flex h-full max-w-[1480px] flex-col justify-center px-4 pb-6 pt-20 sm:px-6 tall:px-12">
      <div>
        <h2 className="pn-h2 max-w-[16ch]">Your biggest pots, replayed.</h2>
        <p className="pn-lede mt-4">
          Net result for every hand you played, with your top ten wins and losses broken down.
        </p>
      </div>

      <div className="pn-bezel mt-[clamp(1.25rem,0.5rem+2.4*var(--u),3rem)]">
        <div className="pn-plate p-[clamp(0.9rem,0.5rem+1.4*var(--u),1.75rem)]">
          <div className="flex items-baseline justify-between">
            <p className="pn-caption">Your running net, hand 1 to {X_MAX} · example data</p>
            <motion.p className="pn-num text-[clamp(1.1rem,0.8rem+1.2*var(--u),2rem)] font-bold text-pn-signal">{total}</motion.p>
          </div>
          <div className="relative mt-4 h-[clamp(8rem,22vh,20rem)]">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden="true">
              <line x1="0" x2={W} y1={sy(0)} y2={sy(0)} stroke="rgb(var(--pn-line) / 0.18)" strokeDasharray="4 6" vectorEffect="non-scaling-stroke" />
              <motion.path
                d={PATH}
                fill="none"
                stroke="rgb(var(--pn-signal))"
                strokeWidth="2.5"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                style={{ pathLength }}
              />
            </svg>
            <Marker p={p} hand={TOP_HANDS.win.hand} positive />
            <Marker p={p} hand={TOP_HANDS.loss.hand} />
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <HandSummary p={p} data={TOP_HANDS.win} at={reachedAt(TOP_HANDS.win.hand)} />
        <HandSummary p={p} data={TOP_HANDS.loss} at={reachedAt(TOP_HANDS.loss.hand)} />
      </div>
    </div>
  );
}
